import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Search,
  Award,
  Loader2,
  CheckCircle,
  XCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCertificates() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState([]);
  const [filteredCerts, setFilteredCerts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchCertificates();
  }, []);

  useEffect(() => {
    let filtered = certificates;
    
    if (searchTerm) {
      filtered = filtered.filter(cert => 
        cert.member_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cert.certificate_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(cert => cert.status === statusFilter);
    }
    
    setFilteredCerts(filtered);
  }, [searchTerm, statusFilter, certificates]);

  const fetchCertificates = async () => {
    try {
      const response = await axios.get(`${API}/certificates`, getAuthHeader());
      setCertificates(response.data);
      setFilteredCerts(response.data);
    } catch (error) {
      console.error('Error fetching certificates:', error);
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (certId, newStatus) => {
    try {
      await axios.put(
        `${API}/certificates/${certId}/status?status=${newStatus}`,
        {},
        getAuthHeader()
      );
      toast.success('Certificate status updated');
      fetchCertificates();
    } catch (error) {
      console.error('Error updating certificate:', error);
      toast.error('Failed to update certificate');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-[#C5A059] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#0E285B] mb-8">
        Certificates
      </h1>

      {/* Filters */}
      <div className="bg-white p-4 rounded-sm border border-slate-200 shadow-sm mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name or certificate number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="cert-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="cert-status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="revoked">Revoked</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Certificates Table */}
      <div className="bg-white rounded-sm border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full admin-table">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left">Certificate #</th>
                <th className="px-6 py-3 text-left">Member</th>
                <th className="px-6 py-3 text-left">Type</th>
                <th className="px-6 py-3 text-left">Issue Date</th>
                <th className="px-6 py-3 text-left">Status</th>
                <th className="px-6 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCerts.map((cert, index) => (
                <tr key={cert.id} data-testid={`cert-row-${index}`}>
                  <td className="px-6 py-4 font-mono text-sm">{cert.certificate_number}</td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-[#0E285B]">{cert.member_name}</p>
                      <p className="text-sm text-slate-500">{cert.country}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-600 capitalize">{cert.membership_type}</td>
                  <td className="px-6 py-4 text-sm text-slate-500">{cert.issue_date}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      cert.status === 'active' ? 'badge-active' : 'badge-inactive'
                    }`}>
                      {cert.status?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      {cert.status === 'active' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(cert.id, 'inactive')}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          data-testid={`deactivate-${index}`}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(cert.id, 'active')}
                          className="text-green-600 border-green-300 hover:bg-green-50"
                          data-testid={`activate-${index}`}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Activate
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredCerts.length === 0 && (
          <div className="text-center py-12">
            <Award className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No certificates found</p>
          </div>
        )}
      </div>
    </div>
  );
}
